import * as React from "react"
import { CalendarIcon } from "lucide-react"

import { cn } from "@/lib/utils"
import { Calendar } from "@/components/ui/calendar"
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@/components/ui/input-group"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

interface DatePickerProps {
  value: string | null | undefined
  onChange: (value: string | null) => void
  placeholder?: string
  disabled?: boolean
  className?: string
}

function formatDate(date: Date | undefined) {
  if (!date) return ""
  return date.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  })
}

function isValidDate(date: Date | undefined) {
  if (!date) return false
  return !isNaN(date.getTime())
}

const ISO_LIKE = /^(\d{4})-(\d{1,2})-(\d{1,2})/

// Parse the leading yyyy-mm-dd as a *calendar* date at local midnight. These fields carry a
// wall-calendar day (no time, no zone), so the engine must not apply a timezone: `new Date("2024-12-31")`
// is UTC midnight (renders the previous day west of UTC) and `new Date("2024-12-31T00:00:00")` is zoneless
// and parsed differently across browsers (local in V8/Chrome, UTC in some Safari builds). Building from the
// y/m/d components sidesteps both, so the same value shows the same day for every user and browser.
// Non-ISO text (e.g. "December 31, 2024", or US "12-31-2024") falls back to the engine parser, which treats
// those forms as local time; the slash retry recovers dash-separated MM-DD-YYYY that Chrome rejects.
function parseCalendarDate(text: string | null | undefined): Date | undefined {
  if (!text) return undefined
  const iso = ISO_LIKE.exec(text.trim())
  if (iso) {
    const [, y, m, d] = iso
    return new Date(Number(y), Number(m) - 1, Number(d))
  }
  const direct = new Date(text)
  if (isValidDate(direct)) return direct
  if (text.includes("-")) {
    const swapped = new Date(text.replace(/-/g, "/"))
    if (isValidDate(swapped)) return swapped
  }
  return undefined
}

function toIsoDateString(date: Date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, "0")
  const d = String(date.getDate()).padStart(2, "0")
  return `${y}-${m}-${d}`
}

function DatePicker({
  value,
  onChange,
  placeholder = "Pick a date",
  disabled,
  className,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false)
  const initial = parseCalendarDate(value)
  const [date, setDate] = React.useState<Date | undefined>(initial)
  const [month, setMonth] = React.useState<Date | undefined>(initial)
  const [text, setText] = React.useState(formatDate(initial))

  // Track the ISO string we last emitted so we can distinguish our own commits
  // from external prop changes (e.g. a parent toggle clearing the field).
  // Only external changes resync the typed text; our own emits leave it alone
  // so the user can keep typing without their input getting reformatted.
  const lastEmittedRef = React.useRef<string | null>(value ?? null)

  React.useEffect(() => {
    const incoming = value ?? null
    if (incoming === lastEmittedRef.current) return
    lastEmittedRef.current = incoming
    const parsed = parseCalendarDate(incoming)
    setDate(parsed)
    setMonth(parsed)
    setText(formatDate(parsed))
  }, [value])

  const emit = (next: Date | undefined) => {
    const iso = next ? toIsoDateString(next) : null
    lastEmittedRef.current = iso
    onChange(iso)
  }

  // Reformat whatever the user typed into the canonical "Month dd, yyyy" form,
  // but only if it parses. Invalid text is left alone so the user can fix it.
  const normalizeTypedText = () => {
    const trimmed = text.trim()
    if (trimmed === "") return
    const parsed = parseCalendarDate(trimmed)
    if (parsed) {
      setText(formatDate(parsed))
    }
  }

  return (
    <InputGroup
      data-disabled={disabled || undefined}
      className={cn("h-8 w-44 data-[disabled=true]:opacity-50", className)}
    >
      <InputGroupInput
        value={text}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => {
          const next = e.target.value
          setText(next)
          if (next.trim() === "") {
            setDate(undefined)
            emit(undefined)
            return
          }
          const parsed = parseCalendarDate(next)
          if (parsed) {
            setDate(parsed)
            setMonth(parsed)
            emit(parsed)
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault()
            setOpen(true)
            return
          }
          if (e.key === "Enter") {
            e.preventDefault()
            normalizeTypedText()
          }
        }}
        onBlur={normalizeTypedText}
      />
      <InputGroupAddon align="inline-end">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <InputGroupButton
              type="button"
              variant="ghost"
              size="icon-xs"
              aria-label="Open calendar"
              disabled={disabled}
            >
              <CalendarIcon />
              <span className="sr-only">Open calendar</span>
            </InputGroupButton>
          </PopoverTrigger>
          <PopoverContent
            className="w-auto overflow-hidden p-0"
            align="end"
            alignOffset={-8}
            sideOffset={10}
          >
            <Calendar
              mode="single"
              selected={date}
              month={month}
              onMonthChange={setMonth}
              captionLayout="dropdown"
              onSelect={(picked) => {
                setDate(picked)
                setText(formatDate(picked))
                emit(picked)
                setOpen(false)
              }}
            />
          </PopoverContent>
        </Popover>
      </InputGroupAddon>
    </InputGroup>
  )
}

export { DatePicker }
